import React, { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '../ui/drawer';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

export const useIsDesktop = (query = '(min-width: 768px)') => {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : true
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    setMatches(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
};

/**
 * Shared educational disclosure surface for the Flippex investment experience:
 * a bottom sheet on mobile, a centered modal on desktop. Used by every
 * "How ... works" explainer so they behave and feel identical.
 */
export const EducationSheet = ({ open, onOpenChange, title, description, testId, children, closeLabel = 'Got it' }) => {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-white max-w-2xl md:max-w-[1008px] w-[calc(100vw-3rem)] rounded-2xl px-8 py-8" data-testid={testId}>
          <DialogHeader className="text-left space-y-1.5">
            <DialogTitle className="font-heading text-xl">{title}</DialogTitle>
            {description && <DialogDescription className="text-sm">{description}</DialogDescription>}
          </DialogHeader>
          <div className="mt-4 max-h-[65vh] overflow-y-auto pr-1">{children}</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-white" data-testid={testId}>
        <DrawerHeader className="text-left px-6 pb-2">
          <DrawerTitle className="font-heading text-lg">{title}</DrawerTitle>
          {description && <DrawerDescription className="text-sm">{description}</DrawerDescription>}
        </DrawerHeader>
        <div className="px-6 pb-6 max-h-[62vh] overflow-y-auto">{children}</div>
        <div className="px-6 pb-6">
          <DrawerClose asChild>
            <Button className="w-full rounded-full py-6" data-testid={`${testId}-close`}>{closeLabel}</Button>
          </DrawerClose>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default EducationSheet;
